export interface ExplorePath {
  slug: string;
  title: string;
  launchReady: boolean;
  premium: boolean;
  lessonIds: string[];
}

export interface LearnChip {
  slug: string;
  label: string;
  open: boolean;
}

export type CourseFilter = 'all' | 'to-read' | 'complete';
export type CourseRead = 'unknown' | 'empty' | 'to-read' | 'complete';

export function searchPaths(paths: ExplorePath[], query: string): ExplorePath[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return paths;
  return paths.filter((path) => path.title.toLowerCase().includes(needle) || path.slug.toLowerCase().includes(needle));
}

export function learnChips(
  paths: Pick<ExplorePath, 'slug' | 'launchReady'>[],
  choices: { slug: string; label: string }[],
): LearnChip[] {
  return choices.map((choice) => {
    const path = paths.find((item) => item.slug === choice.slug);
    return { slug: choice.slug, label: choice.label, open: path?.launchReady === true };
  });
}

/** Launch-ready paths with no completed lesson. Closed paths stay out. No invented dates. */
export function newReleasePaths(paths: ExplorePath[], completedIds: string[]): ExplorePath[] {
  const done = new Set(completedIds);
  return paths
    .filter((path) => path.launchReady && !path.premium && path.lessonIds.length > 0 && path.lessonIds.every((id) => !done.has(id)))
    .slice(0, 3);
}

export function featuredPath(
  paths: ExplorePath[],
  journeySlug: string | null,
): { path: ExplorePath | null; reason: 'yours' | 'free' } {
  const yours = paths.find((path) => path.slug === journeySlug && path.launchReady);
  if (yours) return { path: yours, reason: 'yours' };
  const free = paths.find((path) => path.launchReady && !path.premium);
  return { path: free || null, reason: 'free' };
}

export function courseRead(lessonIds: string[] | null, completedIds: string[]): CourseRead {
  if (!lessonIds) return 'unknown';
  if (lessonIds.length === 0) return 'empty';
  const done = new Set(completedIds);
  return lessonIds.every((id) => done.has(id)) ? 'complete' : 'to-read';
}

export function filterSavedCourses(
  slugs: string[],
  paths: { slug: string; lessonIds: string[] }[],
  completedIds: string[],
  filter: CourseFilter,
): string[] {
  return slugs.filter((slug) => {
    const path = paths.find((item) => item.slug === slug);
    const read = courseRead(path ? path.lessonIds : null, completedIds);
    if (filter === 'all') return true;
    if (filter === 'complete') return read === 'complete';
    return read !== 'complete';
  });
}

export function topicTextOk(text: string): boolean {
  const trimmed = text.trim();
  return trimmed.length >= 2 && trimmed.length <= 200;
}
